import { useState, useEffect } from 'react';
import { Receipt, Plus, Search, Calendar, TrendingUp, Edit2, Trash2, X, DollarSign, Check, CircleDollarSign, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface CompanyExpense {
  id: string;
  expense_type: string;
  description: string;
  amount: number;
  is_recurring: boolean;
  recurring_frequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'yearly';
  is_paused?: boolean;
  is_paid?: boolean;
  paid_at?: string | null;
  expense_date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export function CompanyExpensesView() {
  const toast = useToast();
  const [expenses, setExpenses] = useState<CompanyExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<CompanyExpense | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    expense_type: '',
    description: '',
    amount: '',
    is_recurring: false,
    recurring_frequency: 'monthly' as 'weekly' | 'bi-weekly' | 'monthly' | 'yearly',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    fetchExpenses();
  }, [selectedMonth]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const startDate = `${selectedMonth}-01`;
      const endDate = new Date(selectedMonth + '-01');
      endDate.setMonth(endDate.getMonth() + 1);
      const endDateStr = endDate.toISOString().split('T')[0];

      // Fetch expenses for the selected month
      const { data: monthExpenses, error: monthError } = await supabase
        .from('company_expenses')
        .select('*')
        .gte('expense_date', startDate)
        .lt('expense_date', endDateStr)
        .order('expense_date', { ascending: false });

      if (monthError) throw monthError;

      // Fetch all recurring expenses that started before or during this month (excluding paused ones)
      const { data: recurringExpenses, error: recurringError } = await supabase
        .from('company_expenses')
        .select('*')
        .eq('is_recurring', true)
        .eq('is_paused', false)
        .lte('expense_date', endDateStr);

      if (recurringError) throw recurringError;

      // Generate recurring expense entries for this month.
      // Build dates purely from Y/M/D integers to avoid the ISO-string UTC
      // parsing that would shift "2026-04-01" back to March 31 local time.
      const [selYear, selMonth] = selectedMonth.split('-').map(Number);
      const selectedMonthDate = new Date(selYear, selMonth - 1, 1);
      const generatedExpenses: CompanyExpense[] = [];

      const parseYmd = (s: string): Date => {
        const [y, m, d] = s.split('T')[0].split('-').map(Number);
        return new Date(y, m - 1, d);
      };

      recurringExpenses?.forEach((expense) => {
        const expenseDate = parseYmd(expense.expense_date);

        // Check if this recurring expense should appear in the selected month
        if (expenseDate <= selectedMonthDate && !expense.is_paused) {
          // Check if we already have an actual entry for this month
          const hasActualEntry = monthExpenses?.some(e => {
            const d = parseYmd(e.expense_date);
            return (
              e.expense_type === expense.expense_type &&
              e.description === expense.description &&
              e.amount === expense.amount &&
              d.getMonth() === selectedMonthDate.getMonth() &&
              d.getFullYear() === selectedMonthDate.getFullYear()
            );
          });

          if (!hasActualEntry) {
            // Project to the same day-of-month in the selected month, clamped
            // to the month's last day (e.g. Jan 31 → Feb 28).
            const lastDay = new Date(selYear, selMonth, 0).getDate();
            const day = Math.min(expenseDate.getDate(), lastDay);
            const iso = `${selYear}-${String(selMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // Generate a projected expense entry
            generatedExpenses.push({
              ...expense,
              id: `${expense.id}-projected-${selectedMonth}`,
              expense_date: iso,
            });
          }
        }
      });

      // Combine actual and generated expenses
      const allExpenses = [...(monthExpenses || []), ...generatedExpenses];
      allExpenses.sort((a, b) => new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime());
      
      setExpenses(allExpenses);
    } catch (error: any) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!formData.expense_type.trim()) {
      toast.error('Please enter an expense type');
      return;
    }

    try {
      const expenseData = {
        expense_type: formData.expense_type.trim(),
        description: formData.description.trim(),
        amount,
        is_recurring: formData.is_recurring,
        recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null,
        expense_date: formData.expense_date,
        notes: formData.notes.trim() || null,
      };

      if (editingExpense) {
        const { error } = await supabase
          .from('company_expenses')
          .update(expenseData)
          .eq('id', editingExpense.id);

        if (error) throw error;
        toast.success('Expense updated successfully');
      } else {
        const { error } = await supabase
          .from('company_expenses')
          .insert([expenseData]);

        if (error) throw error;
        toast.success('Expense added successfully');
      }

      fetchExpenses();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving expense:', error);
      toast.error('Failed to save expense');
    }
  };

  const handleTogglePaid = async (expense: CompanyExpense) => {
    try {
      // Projected recurring rows are synthesized in memory — flip them by
      // inserting a concrete row for this month marked paid. We swap the
      // projected entry for the concrete row in place so it stays in the
      // same position instead of jumping when the list re-sorts.
      if (expense.id.includes('projected')) {
        const { data, error } = await supabase
          .from('company_expenses')
          .insert({
            expense_type: expense.expense_type,
            description: expense.description,
            amount: expense.amount,
            is_recurring: false, // concrete row for this month only
            recurring_frequency: null,
            expense_date: expense.expense_date,
            notes: expense.notes || null,
            is_paid: true,
            paid_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (error) throw error;
        setExpenses(prev => prev.map(e => e.id === expense.id ? (data as CompanyExpense) : e));
        toast.success('Marked as paid');
        return;
      }

      const nowPaid = !expense.is_paid;
      const paidAt = nowPaid ? new Date().toISOString() : null;
      const { error } = await supabase
        .from('company_expenses')
        .update({ is_paid: nowPaid, paid_at: paidAt })
        .eq('id', expense.id);
      if (error) throw error;
      // In-place update — no re-fetch, no re-sort, row stays put.
      setExpenses(prev => prev.map(e =>
        e.id === expense.id ? { ...e, is_paid: nowPaid, paid_at: paidAt } : e
      ));
      toast.success(nowPaid ? 'Marked as paid' : 'Marked as unpaid');
    } catch (error: any) {
      console.error('Error toggling paid status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleTogglePause = async (expense: CompanyExpense) => {
    // Don't allow pausing projected expenses
    if (expense.id.includes('projected')) {
      toast.error('Cannot pause a projected expense. Please pause the original recurring expense.');
      return;
    }

    try {
      const newPausedState = !expense.is_paused;
      const { error } = await supabase
        .from('company_expenses')
        .update({ is_paused: newPausedState })
        .eq('id', expense.id);

      if (error) throw error;
      toast.success(newPausedState ? 'Recurring expense paused' : 'Recurring expense resumed');
      fetchExpenses();
    } catch (error: any) {
      console.error('Error toggling pause:', error);
      toast.error('Failed to update expense');
    }
  };

  const handleDelete = async (id: string) => {
    // Don't allow deleting projected expenses
    if (id.includes('projected')) {
      toast.error('Cannot delete a projected expense. Please delete the original recurring expense.');
      return;
    }

    if (!confirm('Are you sure you want to delete this expense?')) return;

    try {
      const { error } = await supabase
        .from('company_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense: CompanyExpense) => {
    setEditingExpense(expense);
    setFormData({
      expense_type: expense.expense_type,
      description: expense.description,
      amount: expense.amount.toString(),
      is_recurring: expense.is_recurring,
      recurring_frequency: expense.recurring_frequency || 'monthly',
      expense_date: expense.expense_date,
      notes: expense.notes || '',
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingExpense(null);
    setFormData({
      expense_type: '',
      description: '',
      amount: '',
      is_recurring: false,
      recurring_frequency: 'monthly',
      expense_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const filteredExpenses = expenses.filter(expense => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      expense.expense_type.toLowerCase().includes(query) ||
      expense.description.toLowerCase().includes(query) ||
      expense.notes?.toLowerCase().includes(query)
    );
  });

  const getTotalStats = () => {
    const total = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
    const recurring = filteredExpenses.filter(e => e.is_recurring).reduce((sum, e) => sum + e.amount, 0);
    const oneTime = filteredExpenses.filter(e => !e.is_recurring).reduce((sum, e) => sum + e.amount, 0);
    // Projected recurring rows are always "unpaid" (no concrete row exists yet).
    const paid = filteredExpenses.filter(e => e.is_paid && !e.id.includes('projected')).reduce((sum, e) => sum + e.amount, 0);
    const unpaid = filteredExpenses
      .filter(e => !e.is_paid || e.id.includes('projected'))
      .reduce((sum, e) => sum + e.amount, 0);
    return { total, recurring, oneTime, paid, unpaid, count: filteredExpenses.length };
  };

  const stats = getTotalStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Company Expenses</h2>
            <p className="text-blue-100">Track and manage company expenses and recurring payments</p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <Receipt className="w-12 h-12" />
          </div>
        </div>
      </div>

      {/* Paid / Unpaid headline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-800">Paid this month</p>
              <p className="text-3xl font-bold text-emerald-700">${stats.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <CircleDollarSign className="w-10 h-10 text-emerald-600" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-800">Unpaid this month</p>
              <p className="text-3xl font-bold text-amber-700">${stats.unpaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{stats.count}</p>
            </div>
            <Receipt className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">${stats.total.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Recurring</p>
              <p className="text-2xl font-bold text-gray-900">${stats.recurring.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">One-Time</p>
              <p className="text-2xl font-bold text-gray-900">${stats.oneTime.toLocaleString()}</p>
            </div>
            <Calendar className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search expenses..."
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="w-5 h-5" />
          Add Expense
        </button>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No expenses found for this month. Add your first expense to get started.
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense) => (
                  <tr
                    key={expense.id}
                    className={`transition-colors ${
                      expense.is_paid && !expense.id.includes('projected')
                        ? 'bg-emerald-100 hover:bg-emerald-200 border-l-4 border-emerald-500'
                        : expense.is_paused
                          ? 'bg-red-50 hover:bg-red-100'
                          : expense.is_recurring
                            ? 'bg-orange-50 hover:bg-orange-100'
                            : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        const [y, m, d] = expense.expense_date.split('T')[0].split('-').map(Number);
                        return new Date(y, m - 1, d).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        });
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {expense.expense_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{expense.description}</div>
                      {expense.notes && (
                        <div className="text-xs text-gray-500 mt-1">{expense.notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>${expense.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        {expense.is_paid && !expense.id.includes('projected') && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <Check className="w-3 h-3" /> PAID
                          </span>
                        )}
                      </div>
                      {expense.is_paid && expense.paid_at && (
                        <div className="text-xs text-emerald-700 mt-0.5">
                          on {new Date(expense.paid_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {expense.is_recurring ? (
                        <div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            expense.is_paused 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {expense.is_paused ? 'Paused' : 'Recurring'}
                          </span>
                          <div className="text-xs text-gray-500 mt-1 capitalize">
                            {expense.recurring_frequency?.replace('-', ' ')}
                          </div>
                        </div>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          One-Time
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const isPaidRow = expense.is_paid && !expense.id.includes('projected');
                          return (
                            <button
                              onClick={() => handleTogglePaid(expense)}
                              title={isPaidRow ? 'Mark as unpaid' : 'Mark as paid'}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                                isPaidRow
                                  ? 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                                  : 'bg-white text-slate-700 border-slate-300 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                              }`}
                            >
                              {isPaidRow ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                              {isPaidRow ? 'Mark as unpaid' : 'Mark paid'}
                            </button>
                          );
                        })()}
                        {expense.is_recurring && (
                          <button
                            onClick={() => handleTogglePause(expense)}
                            className={`${
                              expense.is_paused 
                                ? 'text-green-600 hover:text-green-900' 
                                : 'text-orange-600 hover:text-orange-900'
                            }`}
                            title={expense.is_paused ? 'Resume recurring expense' : 'Pause recurring expense'}
                          >
                            {expense.is_paused ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="6" y="4" width="4" height="16"></rect>
                                <rect x="14" y="4" width="4" height="16"></rect>
                              </svg>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(expense)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit expense"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete expense"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Expense Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expense Type
                  </label>
                  <input
                    type="text"
                    value={formData.expense_type}
                    onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                    placeholder="e.g., Office Rent, Insurance, Utilities"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the expense"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Expense Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expense Date
                  </label>
                  <input
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Recurring Payment */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                    This is a recurring payment
                  </label>
                </div>

                {/* Recurring Frequency */}
                {formData.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recurring Frequency
                    </label>
                    <select
                      value={formData.recurring_frequency}
                      onChange={(e) => setFormData({ ...formData, recurring_frequency: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="bi-weekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Add any additional notes..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? 'Update Expense' : 'Add Expense'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
